import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BetaLockOverlay } from '../../components/ui/BetaLockOverlay';
import {
    Search, Star, MapPin, Phone, Mail, ExternalLink, Shield, ChevronRight,
    Filter, X, Award, Droplets, Zap, Paintbrush, Hammer, Grid3x3, Home,
    KeyRound, Building2, ClipboardCheck, Ruler, Palette, Truck, TreePine,
    Sparkles, Thermometer, DoorOpen, Scale, Landmark, ArrowLeft, Send,
    BadgeCheck, MessageSquare, CookingPot, Users, TrendingUp, ChevronDown
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
    Droplets, Zap, Paintbrush, Hammer, Grid3x3, Home, KeyRound, Building2,
    ClipboardCheck, Ruler, Palette, Truck, TreePine, Sparkles, Thermometer,
    DoorOpen, Scale, Landmark, CookingPot,
};

const PLAN_COLORS = {
    premium: 'from-[#7C3AED] to-[#F59E0B]',
    starter: 'from-[#7C3AED] to-[#7C3AED]',
    free: 'from-slate-600 to-slate-600',
};

function StarRating({ rating, size = 16 }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    className={`${i <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`}
                    size={size}
                />
            ))}
        </div>
    );
}

function SpecialtyIcon({ iconName, className = "w-5 h-5" }) {
    const Icon = ICON_MAP[iconName] || Building2;
    return <Icon className={className} />;
}

function ProfessionalCard({ pro, onClick }) {
    const specialty = pro.specialty;
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.3 }}
        >
            <Card
                data-testid={`pro-card-${pro.id}`}
                onClick={() => onClick(pro)}
                className="cursor-pointer bg-card border-border hover:border-[#7C3AED]/40 transition-all duration-300 overflow-hidden group"
            >
                {/* Top gradient bar */}
                <div className={`h-1 bg-gradient-to-r ${PLAN_COLORS[pro.subscription_plan] || PLAN_COLORS.free}`} />
                
                <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${PLAN_COLORS[pro.subscription_plan] || PLAN_COLORS.free} flex items-center justify-center`}>
                                <span className="text-white font-bold text-lg">
                                    {pro.company_name?.charAt(0) || '?'}
                                </span>
                            </div>
                            <div>
                                <h3 className="font-semibold text-foreground text-sm group-hover:text-[#F59E0B] transition-colors">
                                    {pro.company_name}
                                </h3>
                                <p className="text-xs text-muted-foreground">{pro.name}</p>
                            </div>
                        </div>
                        {pro.is_verified && (
                            <Badge data-testid="verified-badge" className="bg-[#7C3AED]/20 text-[#F59E0B] border-0 text-[10px] gap-1">
                                <BadgeCheck className="w-3 h-3" /> Certifie
                            </Badge>
                        )}
                    </div>

                    {/* Description */}
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
                        {pro.description}
                    </p>

                    {/* Location + Specialty */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-[#F59E0B]" />
                            {pro.address?.city || 'Non renseigne'}
                        </span>
                        <span className="flex items-center gap-1">
                            <SpecialtyIcon iconName={ICON_MAP[pro.specialty] ? pro.specialty : 'Building2'} className="w-3 h-3 text-[#7C3AED]" />
                            {pro.service_radius_km}km
                        </span>
                    </div>

                    {/* Rating + Reviews */}
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                            <StarRating rating={pro.rating_avg} size={14} />
                            <span className="text-sm font-semibold text-foreground">{pro.rating_avg}</span>
                            <span className="text-xs text-muted-foreground">({pro.review_count} avis)</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#F59E0B] transition-colors" />
                    </div>

                    {/* Certifications */}
                    {pro.certifications?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {pro.certifications.slice(0, 3).map((cert, i) => (
                                <span key={i} className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">
                                    {cert}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </Card>
        </motion.div>
    );
}

function ProfessionalDetail({ pro, onBack, user }) {
    const [reviews, setReviews] = useState([]);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewData, setReviewData] = useState({ rating: 5, comment: '', tags: [] });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/professionals/${pro.id}/reviews`)
            .then(r => r.json())
            .then(d => setReviews(d.reviews || []))
            .catch(() => {});
    }, [pro.id]);

    const submitReview = async () => {
        if (!user) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API}/api/professionals/${pro.id}/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(reviewData),
            });
            if (res.ok) {
                const data = await res.json();
                setReviews(prev => [data.review, ...prev]);
                setShowReviewForm(false);
                setReviewData({ rating: 5, comment: '', tags: [] });
            }
        } catch (e) { console.error(e); }
        setSubmitting(false);
    };

    const specialtyLabel = pro.specialty;

    return (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {/* Back button */}
            <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-to-list">
                <ArrowLeft className="w-4 h-4" /> Retour a l'annuaire
            </button>

            {/* Profile header */}
            <Card className="bg-card border-border overflow-hidden mb-6">
                <div className={`h-2 bg-gradient-to-r ${PLAN_COLORS[pro.subscription_plan] || PLAN_COLORS.free}`} />
                <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${PLAN_COLORS[pro.subscription_plan] || PLAN_COLORS.free} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-white font-bold text-3xl">{pro.company_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-start justify-between flex-wrap gap-2">
                                <div>
                                    <h1 className="text-2xl font-bold text-foreground">{pro.company_name}</h1>
                                    <p className="text-muted-foreground">{pro.name}</p>
                                </div>
                                {pro.is_verified && (
                                    <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 gap-1 px-3 py-1">
                                        <BadgeCheck className="w-4 h-4" /> Homly Certifie
                                    </Badge>
                                )}
                            </div>
                            <p className="text-muted-foreground mt-3 leading-relaxed">{pro.description}</p>

                            {/* Contact info */}
                            <div className="flex flex-wrap gap-4 mt-4 text-sm">
                                <span className="flex items-center gap-2 text-muted-foreground">
                                    <MapPin className="w-4 h-4 text-[#F59E0B]" />
                                    {pro.address?.city}, {pro.address?.postal_code} ({pro.address?.department})
                                </span>
                                <span className="flex items-center gap-2 text-muted-foreground">
                                    <Phone className="w-4 h-4 text-[#7C3AED]" /> {pro.phone}
                                </span>
                                <span className="flex items-center gap-2 text-muted-foreground">
                                    <Mail className="w-4 h-4 text-[#7C3AED]" /> {pro.email}
                                </span>
                            </div>

                            {/* Stats row */}
                            <div className="flex flex-wrap gap-6 mt-5">
                                <div className="flex items-center gap-2">
                                    <StarRating rating={pro.rating_avg} />
                                    <span className="font-bold text-foreground">{pro.rating_avg}</span>
                                    <span className="text-muted-foreground text-sm">({pro.review_count} avis)</span>
                                </div>
                                <span className="text-muted-foreground text-sm flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Rayon: {pro.service_radius_km}km
                                </span>
                            </div>

                            {/* Certifications */}
                            {pro.certifications?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-4">
                                    {pro.certifications.map((cert, i) => (
                                        <Badge key={i} variant="outline" className="border-[#7C3AED]/30 text-[#F59E0B]">
                                            <Award className="w-3 h-3 mr-1" /> {cert}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Reviews section */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#F59E0B]" />
                    Avis des agents ({reviews.length})
                </h2>
                {user && !showReviewForm && (
                    <Button onClick={() => setShowReviewForm(true)} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white" data-testid="add-review-btn">
                        Laisser un avis
                    </Button>
                )}
            </div>

            {/* Review form */}
            <AnimatePresence>
                {showReviewForm && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                        <Card className="bg-card border-[#7C3AED]/30 p-5 mb-4">
                            <h3 className="font-semibold mb-3">Votre avis</h3>
                            <div className="flex gap-1 mb-3">
                                {[1,2,3,4,5].map(i => (
                                    <button key={i} onClick={() => setReviewData(d => ({...d, rating: i}))}>
                                        <Star className={`w-6 h-6 ${i <= reviewData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} />
                                    </button>
                                ))}
                            </div>
                            <textarea
                                data-testid="review-comment"
                                className="w-full bg-muted border border-border rounded-lg p-3 text-sm text-foreground min-h-[80px] mb-3"
                                placeholder="Decrivez votre experience avec ce professionnel (min 10 caracteres)..."
                                value={reviewData.comment}
                                onChange={e => setReviewData(d => ({...d, comment: e.target.value}))}
                            />
                            <div className="flex gap-2">
                                <Button onClick={submitReview} disabled={submitting || reviewData.comment.length < 10} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white">
                                    {submitting ? 'Envoi...' : 'Publier'}
                                </Button>
                                <Button variant="outline" onClick={() => setShowReviewForm(false)}>Annuler</Button>
                            </div>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Reviews list */}
            <div className="space-y-3">
                {reviews.map(review => (
                    <Card key={review.id} className="bg-card border-border p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center text-white text-xs font-bold">
                                    {review.reviewer_name?.charAt(0) || '?'}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground">{review.reviewer_name}</p>
                                    <p className="text-xs text-muted-foreground">{review.reviewer_agency}</p>
                                </div>
                            </div>
                            <StarRating rating={review.rating} size={12} />
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{review.comment}</p>
                        {review.tags?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                                {review.tags.map((tag, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-[#7C3AED]/10 text-[#F59E0B] rounded text-[10px]">{tag}</span>
                                ))}
                            </div>
                        )}
                    </Card>
                ))}
                {reviews.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Aucun avis pour le moment</p>
                )}
            </div>
        </motion.div>
    );
}

export default function HomlyProPage() {
    const { user } = useAuth();
    const [professionals, setProfessionals] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedPro, setSelectedPro] = useState(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const fetchProfessionals = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: page.toString(), limit: '12' });
            if (searchQuery) params.set('q', searchQuery);
            if (selectedSpecialty) params.set('specialty', selectedSpecialty);
            if (selectedCity) params.set('city', selectedCity);
            if (verifiedOnly) params.set('verified_only', 'true');

            const res = await fetch(`${API}/api/professionals/search?${params}`);
            const data = await res.json();
            setProfessionals(data.professionals || []);
            setTotal(data.total || 0);
            setPages(data.pages || 1);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, [page, searchQuery, selectedSpecialty, selectedCity, verifiedOnly]);

    useEffect(() => {
        fetch(`${API}/api/professionals/specialties`)
            .then(r => r.json())
            .then(d => setSpecialties(d.specialties || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchProfessionals();
    }, [fetchProfessionals]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchProfessionals();
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedSpecialty('');
        setSelectedCity('');
        setVerifiedOnly(false);
        setPage(1);
    };

    const activeFilterCount = [searchQuery, selectedSpecialty, selectedCity, verifiedOnly].filter(Boolean).length;

    if (selectedPro) {
        return (
            <div className="max-w-4xl mx-auto">
                <ProfessionalDetail pro={selectedPro} onBack={() => setSelectedPro(null)} user={user} />
            </div>
        );
    }

    return (
        <BetaLockOverlay featureName="L'annuaire Homly Partenaires" productionOnly={true}>
        <div data-testid="homly-pro-page" className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center">
                            <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground font-heading">Homly Partenaires</h1>
                            <p className="text-sm text-muted-foreground">Annuaire des professionnels certifies</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#7C3AED]/30 text-[#F59E0B] hover:bg-[#7C3AED]/10"
                        data-testid="share-pro-link"
                        onClick={() => {
                            const url = `${window.location.origin}/pro`;
                            navigator.clipboard.writeText(url).then(() => {
                                const btn = document.querySelector('[data-testid="share-pro-link"]');
                                if (btn) { btn.textContent = 'Lien copie !'; setTimeout(() => { btn.textContent = 'Inviter un artisan'; }, 2000); }
                            });
                        }}
                    >
                        <Send className="w-3.5 h-3.5 mr-1.5" /> Inviter un artisan
                    </Button>
                    <div className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border">
                        <TrendingUp className="w-4 h-4 text-[#F59E0B]" />
                        <span className="text-foreground font-semibold">{total}</span>
                        <span className="text-muted-foreground">professionnels</span>
                    </div>
                </div>
            </div>

            {/* Search & Filters */}
            <Card className="bg-card border-border p-4">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            data-testid="pro-search-input"
                            placeholder="Rechercher un professionnel, une entreprise..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white" data-testid="pro-search-btn">
                        <Search className="w-4 h-4 mr-2" /> Rechercher
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className={activeFilterCount > 0 ? 'border-[#F59E0B] text-[#F59E0B]' : ''}
                        data-testid="pro-filter-btn"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filtres {activeFilterCount > 0 && `(${activeFilterCount})`}
                        <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </Button>
                </form>

                {/* Expanded filters */}
                <AnimatePresence>
                    {showFilters && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-border">
                                <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Specialite</label>
                                    <select
                                        data-testid="specialty-filter"
                                        value={selectedSpecialty}
                                        onChange={e => { setSelectedSpecialty(e.target.value); setPage(1); }}
                                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                                    >
                                        <option value="">Toutes les specialites</option>
                                        {specialties.map(s => (
                                            <option key={s.id} value={s.id}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Ville</label>
                                    <Input
                                        data-testid="city-filter"
                                        placeholder="Ex: Paris, Lyon..."
                                        value={selectedCity}
                                        onChange={e => { setSelectedCity(e.target.value); setPage(1); }}
                                    />
                                </div>
                                <div className="flex items-end gap-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={verifiedOnly}
                                            onChange={e => { setVerifiedOnly(e.target.checked); setPage(1); }}
                                            className="accent-[#7C3AED]"
                                        />
                                        <span className="text-sm text-foreground">Certifies uniquement</span>
                                    </label>
                                    {activeFilterCount > 0 && (
                                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#F59E0B]">
                                            <X className="w-3 h-3 mr-1" /> Effacer
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Card>

            {/* Specialty quick filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {specialties.slice(0, 8).map(s => {
                    const Icon = ICON_MAP[s.icon] || Building2;
                    const isActive = selectedSpecialty === s.id;
                    return (
                        <button
                            key={s.id}
                            onClick={() => { setSelectedSpecialty(isActive ? '' : s.id); setPage(1); }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                                isActive
                                    ? 'bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white'
                                    : 'bg-card border border-border text-muted-foreground hover:border-[#7C3AED]/40'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {s.label}
                        </button>
                    );
                })}
            </div>

            {/* Results grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <Card key={i} className="bg-card border-border p-5 animate-pulse">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-xl bg-muted" />
                                <div className="space-y-2 flex-1">
                                    <div className="h-4 bg-muted rounded w-3/4" />
                                    <div className="h-3 bg-muted rounded w-1/2" />
                                </div>
                            </div>
                            <div className="h-8 bg-muted rounded mb-3" />
                            <div className="h-4 bg-muted rounded w-2/3" />
                        </Card>
                    ))}
                </div>
            ) : professionals.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {professionals.map(pro => (
                            <ProfessionalCard key={pro.id} pro={pro} onClick={setSelectedPro} />
                        ))}
                    </div>

                    {/* Pagination */}
                    {pages > 1 && (
                        <div className="flex justify-center gap-2 mt-6">
                            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPage(p)}
                                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                                        p === page
                                            ? 'bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white'
                                            : 'bg-card border border-border text-muted-foreground hover:border-[#7C3AED]/40'
                                    }`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-16">
                    <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Aucun professionnel trouve</h3>
                    <p className="text-muted-foreground mb-4">Essayez d'elargir vos criteres de recherche</p>
                    <Button variant="outline" onClick={clearFilters}>Effacer les filtres</Button>
                </div>
            )}
        </div>
        </BetaLockOverlay>
    );
}
